import { type Email, type List, type Page } from '@prisma/client'
import { useFormAction, useNavigation } from '@remix-run/react'
import { clsx, type ClassValue } from 'clsx'
import { differenceInCalendarDays } from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'
import _ from 'lodash'
import { type Dispatch, useEffect, useMemo, useRef, useState } from 'react'
import { type DateRange } from 'react-day-picker'
import { useSpinDelay } from 'spin-delay'
import { extendTailwindMerge } from 'tailwind-merge'
import tinycolor from 'tinycolor2'
import { type NumberFilter } from '#app/components/ui/data-table-number-filter.js'
import { type TinySub } from '#app/routes/emails+/campaigns+/$campaignId.send.js'
import { EMAIL_TYPES, PAGE_TYPES } from './constants.ts'
import { extendedTheme } from './extended-theme.ts'

export const camelCaseToReadable = (str: string): string => {
	const readable = _.startCase(_.camelCase(str))
	return readable.charAt(0).toUpperCase() + readable.slice(1).toLowerCase()
}

export function getUserImgSrc(imageId?: string | null) {
	return imageId ? `/resources/user-images/${imageId}` : '/img/user.png'
}

export function getListImgSrc(imageId?: string | null) {
	return imageId ? `/resources/list-images/${imageId}` : '/img/user.png'
}

export function getListIconSrc(src?: string | null) {
	return src || '/img/wt.png'
}

export function getListFaviconSrc(src?: string | null) {
	return src || '/img/wt.png'
}

export function getListLogoSrc(imageId?: string | null) {
	return imageId ? `/resources/list-logo/${imageId}` : '/img/user.png'
}

export function getListOgImageSrc(src?: string | null) {
	return src || '/img/placeholder/open-graph-image.png'
}

export function getImageSrc(imageId?: string | null) {
	return imageId ? `/resources/images/${imageId}` : '/img/placeholder-image.jpg'
}

export function getNoteImgSrc(imageId: string) {
	return `/resources/note-images/${imageId}`
}

export function getErrorMessage(error: unknown) {
	if (typeof error === 'string') return error
	if (
		error &&
		typeof error === 'object' &&
		'message' in error &&
		typeof error.message === 'string'
	) {
		return error.message
	}
	console.error('Unable to get error message for error', error)
	return 'Unknown Error'
}

function formatColors() {
	const colors = []
	for (const [key, color] of Object.entries(extendedTheme.colors)) {
		if (typeof color === 'string') {
			colors.push(key)
		} else {
			const colorGroup = Object.keys(color).map(subKey =>
				subKey === 'DEFAULT' ? '' : subKey,
			)
			colors.push({ [key]: colorGroup })
		}
	}
	return colors
}

const customTwMerge = extendTailwindMerge<string, string>({
	extend: {
		theme: {
			colors: formatColors(),
			borderRadius: Object.keys(extendedTheme.borderRadius),
		},
		classGroups: {
			'font-size': [
				{
					text: Object.keys(extendedTheme.fontSize),
				},
			],
		},
	},
})

export function cn(...inputs: ClassValue[]) {
	return customTwMerge(clsx(inputs))
}

export function getDomainUrl(request: Request) {
	const host =
		request.headers.get('X-Forwarded-Host') ??
		request.headers.get('host') ??
		new URL(request.url).host
	const protocol = host.includes('localhost') ? 'http' : 'https'
	return `${protocol}://${host}`
}

export function getReferrerRoute(request: Request) {
	// spelling errors and whatever makes this annoyingly inconsistent
	// in my own testing, `referer` returned the right value, but 🤷‍♂️
	const referrer =
		request.headers.get('referer') ??
		request.headers.get('referrer') ??
		request.referrer
	const domain = getDomainUrl(request)
	if (referrer?.startsWith(domain)) {
		return referrer.slice(domain.length)
	} else {
		return '/'
	}
}

/**
 * Merge multiple headers objects into one (uses set so headers are overridden)
 */
export function mergeHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const merged = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			merged.set(key, value)
		}
	}
	return merged
}

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const combined = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			combined.append(key, value)
		}
	}
	return combined
}

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
	...responseInits: Array<ResponseInit | null | undefined>
) {
	let combined: ResponseInit = {}
	for (const responseInit of responseInits) {
		combined = {
			...responseInit,
			headers: combineHeaders(combined.headers, responseInit?.headers),
		}
	}
	return combined
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
	formAction,
	formMethod = 'POST',
	state = 'non-idle',
}: {
	formAction?: string
	formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
	state?: 'submitting' | 'loading' | 'non-idle'
} = {}) {
	const contextualFormAction = useFormAction()
	const navigation = useNavigation()
	const isPendingState =
		state === 'non-idle'
			? navigation.state !== 'idle'
			: navigation.state === state
	return (
		isPendingState &&
		navigation.formAction === (formAction ?? contextualFormAction) &&
		navigation.formMethod === formMethod
	)
}

/**
 * This combines useSpinDelay (from https://npm.im/spin-delay) and useIsPending
 * from our own utilities to give you a nice way to show a loading spinner for
 * a minimum amount of time, even if the request finishes right after the delay.
 *
 * This avoids a flash of loading state regardless of how fast or slow the
 * request is.
 */
export function useDelayedIsPending({
	formAction,
	formMethod,
	delay = 400,
	minDuration = 300,
}: Parameters<typeof useIsPending>[0] &
	Parameters<typeof useSpinDelay>[1] = {}) {
	const isPending = useIsPending({ formAction, formMethod })
	const delayedIsPending = useSpinDelay(isPending, {
		delay,
		minDuration,
	})
	return delayedIsPending
}

function callAll<Args extends Array<unknown>>(
	...fns: Array<((...args: Args) => unknown) | undefined>
) {
	return (...args: Args) => fns.forEach(fn => fn?.(...args))
}

/**
 * Use this hook with a button and it will make it so the first click sets a
 * `doubleCheck` state to true, and the second click will actually trigger the
 * `onClick` handler. This allows you to have a button that can be like a
 * "are you sure?" experience for the user before doing destructive operations.
 */
export function useDoubleCheck() {
	const [doubleCheck, setDoubleCheck] = useState(false)

	function getButtonProps(
		props?: React.ButtonHTMLAttributes<HTMLButtonElement>,
	) {
		const onBlur: React.ButtonHTMLAttributes<HTMLButtonElement>['onBlur'] =
			() => setDoubleCheck(false)

		const onClick: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'] =
			doubleCheck
				? undefined
				: e => {
						e.preventDefault()
						setDoubleCheck(true)
						const button = e.currentTarget
						button.classList.add(
							'bg-red-100',
							'text-red-700',
							'hover:bg-red-50',
						)
					}

		const onKeyUp: React.ButtonHTMLAttributes<HTMLButtonElement>['onKeyUp'] =
			e => {
				if (e.key === 'Escape') {
					setDoubleCheck(false)
					const button = e.currentTarget
					button.classList.add('bg-red-100', 'text-red-700', 'hover:bg-red-50')
				}
			}

		return {
			...props,
			onBlur: callAll(onBlur, props?.onBlur),
			onClick: callAll(onClick, props?.onClick),
			onKeyUp: callAll(onKeyUp, props?.onKeyUp),
		}
	}

	return { doubleCheck, getButtonProps }
}

/**
 * Simple debounce implementation
 */
function debounce<Callback extends (...args: Parameters<Callback>) => void>(
	fn: Callback,
	delay: number,
) {
	let timer: ReturnType<typeof setTimeout> | null = null
	return (...args: Parameters<Callback>) => {
		if (timer) clearTimeout(timer)
		timer = setTimeout(() => {
			fn(...args)
		}, delay)
	}
}

/**
 * Debounce a callback function
 */
export function useDebounce<
	Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
	const callbackRef = useRef(callback)
	useEffect(() => {
		callbackRef.current = callback
	})
	return useMemo(
		() =>
			debounce(
				(...args: Parameters<Callback>) => callbackRef.current(...args),
				delay,
			),
		[delay],
	)
}

export async function downloadFile(url: string, retries: number = 0) {
	const MAX_RETRIES = 3
	try {
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`Failed to fetch image with status ${response.status}`)
		}
		const contentType = response.headers.get('content-type') ?? 'image/jpg'
		const blob = Buffer.from(await response.arrayBuffer())
		return { contentType, blob }
	} catch (e) {
		if (retries > MAX_RETRIES) throw e
		return downloadFile(url, retries + 1)
	}
}

export async function copyToClipboard(text: string) {
	try {
		await navigator.clipboard.writeText(text)
	} catch (err) {
		console.error('Failed to copy: ', err)
	}
}

export async function copy(
	text: string,
	setCopy: Dispatch<React.SetStateAction<boolean>>,
) {
	try {
		await navigator.clipboard.writeText(text)
		setCopy(true)

		setTimeout(() => {
			setCopy(false)
		}, 2000)
	} catch (err) {
		console.error('Failed to copy: ', err)
	}
}

export function getOrdinalSuffixOnly(number: number): string {
	let suffix = 'th'

	const j = number % 10,
		k = number % 100
	if (j === 1 && k !== 11) {
		suffix = 'st'
	}
	if (j === 2 && k !== 12) {
		suffix = 'nd'
	}
	if (j === 3 && k !== 13) {
		suffix = 'rd'
	}
	return suffix
}

export function validateEmail(email: unknown): email is string {
	return (
		typeof email === 'string' &&
		email.length > 3 &&
		email.includes('@') &&
		email !== ''
	)
}

export function setSearchParamsString(
	searchParams: URLSearchParams,
	changes: Record<string, string | number | undefined>,
) {
	const newSearchParams = new URLSearchParams(searchParams)
	for (const [key, value] of Object.entries(changes)) {
		if (value === undefined) {
			newSearchParams.delete(key)
			continue
		}
		newSearchParams.set(key, String(value))
	}
	return Array.from(newSearchParams.entries())
		.map(([key, value]) =>
			value ? `${key}=${encodeURIComponent(value)}` : key,
		)
		.join('&')
}

export function formatRelativeDate(dateInput: Date) {
	const timeZone = 'UTC'
	const date = utcToZonedTime(dateInput, timeZone)
	const now = utcToZonedTime(new Date(), timeZone)

	const daysDifference = differenceInCalendarDays(now, date)

	if (daysDifference === 0) {
		return `Today, ${format(date, 'h:mm a', { timeZone })}`
	} else if (daysDifference === 1) {
		return `Yesterday, ${format(date, 'h:mm a', { timeZone })}`
	} else {
		const dayWithSuffix = format(date, 'do')
		return `${format(date, 'MMM')} ${dayWithSuffix}, ${format(date, 'h:mm a', { timeZone })}`
	}
}

export function formatRelativeDay(dateInput: Date) {
	const date = new Date(dateInput)
	const now = new Date()
	const yesterday = new Date(now.getTime() - 86400000)

	// Check if date is today
	if (date.toDateString() === now.toDateString()) {
		return 'Today'
	}
	// Check if date is yesterday
	else if (date.toDateString() === yesterday.toDateString()) {
		return 'Yesterday'
	}
	// For other days
	else {
		const formatter = new Intl.DateTimeFormat('en', {
			day: 'numeric',
			month: 'short',
		})

		const suffixes = ['th', 'st', 'nd', 'rd']
		const v = date.getDate() % 100
		const suffix = (v - 20) % 10
		const dayWithSuffix =
			date.getDate() + suffixes[v < 20 ? 0 : suffix > 3 ? 0 : suffix + 1]

		return formatter.format(date).replace(/\d+/, dayWithSuffix)
	}
}

export const chunkArray = (array: TinySub[], size: number) => {
	const result = []
	for (let i = 0; i < array.length; i += size) {
		result.push(array.slice(i, i + size))
	}
	return result
}

export function getSizeInBytes(obj: any) {
	const jsonString = JSON.stringify(obj)
	return Buffer.byteLength(jsonString, 'utf8')
}

export function splitPayload(events: any, maxChunkSize: number) {
	let currentChunk = []
	let currentChunkSize = 0
	const chunks = []

	for (const event of events) {
		const eventSize = getSizeInBytes(event)

		if (currentChunkSize + eventSize > maxChunkSize) {
			chunks.push(currentChunk)
			currentChunk = []
			currentChunkSize = 0
		}

		currentChunk.push(event)
		currentChunkSize += eventSize
	}

	if (currentChunk.length > 0) {
		chunks.push(currentChunk)
	}

	return chunks
}

/**
 * Calculates the percentage of a numerator over a denominator, ensuring the result does not exceed 100%.
 *
 * @param numerator - The numerator value.
 * @param denominator - The denominator value.
 * @returns A string representing the percentage with two decimal places.
 */
export function calculatePercentage(
	numerator: number,
	denominator: number,
): number {
	// Handle cases where denominator is zero or inputs are invalid
	if (!isFinite(numerator) || !isFinite(denominator) || denominator === 0) {
		return 0
	}

	// Calculate the raw percentage
	let percentage = (numerator / denominator) * 100

	// Optionally, handle negative percentages if applicable
	percentage = Math.max(0, percentage)

	// Clamp the percentage to a maximum of 100%
	percentage = Math.min(percentage, 100)

	// Return the percentage formatted to two decimal places as a number
	return percentage / 100
}

/**
 * Converts an 8-digit hex color to a 6-digit hex color by blending it over a background color.
 *
 * @param {string} foregroundColor - The 8-digit hex color (e.g., "#RRGGBBAA").
 * @param {string} backgroundColor - The 6-digit hex background color (e.g., "#RRGGBB").
 * @returns {string} - The resulting 6-digit hex color after blending.
 */
export function getColor(foregroundColor?: string, backgroundColor?: string) {
	const fgColor = tinycolor(foregroundColor)
	const bgColor = tinycolor(backgroundColor)

	const blendedColor = tinycolor
		.mix(bgColor, fgColor, fgColor.getAlpha() * 100)
		.toHexString()
		.toUpperCase()

	return blendedColor
}

export function getPageURL(
	target: any,
	domain: string,
	shortId: string,
	url: string,
) {
	let previewURL = ''

	switch (target.type) {
		case PAGE_TYPES.SIGNUP:
			previewURL = target.customUrlToggle
				? `${target.customUrl}`
				: `${domain}/${url}`
			break
		default:
			previewURL = target.customUrlToggle
				? `${target.customUrl}?m=${shortId}`
				: `${domain}/me/${shortId}`
			break
	}

	return previewURL
}

export const getJourney = (
	list: Partial<List> & { pages: Page[]; emails: Email[] },
) => {
	const SIGNUP_PAGE = list.pages.find(page => page.type === PAGE_TYPES.SIGNUP)
	const SUBSCRIBER_PAGE = list.pages.find(
		page => page.type === PAGE_TYPES.SUBSCRIBER,
	)
	const REFERRAL_PAGE = list.pages.find(
		page => page.type === PAGE_TYPES.REFERRAL,
	)
	const VERIFICATION_EMAIL = list.emails.find(
		email => email.type === EMAIL_TYPES.VERIFICATION,
	)
	// const INVITATION_EMAIL = list.emails.find(
	// 	email => email.type === EMAIL_TYPES.INVITATION,
	// )
	// const REDEEMED_EMAIL = list.emails.find(
	// 	email => email.type === EMAIL_TYPES.REDEEMED,
	// )

	if (list.referrals) {
		return [
			{
				...SIGNUP_PAGE,
				name: 'Sign up form',
				icon: 'page',
			},
			{
				...VERIFICATION_EMAIL,
				name: 'Confirmation email',
				icon: 'email',
			},
			{
				...REFERRAL_PAGE,
				name: 'Referral page',
				icon: 'page',
			},
			// {
			// 	...INVITATION_EMAIL,
			// 	name: 'Invitation email',
			// 	icon: 'email',
			// },
			// {
			// 	...REDEEMED_EMAIL,
			// 	name: 'Redeemed email',
			// 	icon: 'email',
			// },
		]
	} else {
		return [
			{
				...SIGNUP_PAGE,
				name: 'Sign up form',
				icon: 'page',
			},
			{
				...VERIFICATION_EMAIL,
				name: 'Confirmation email',
				icon: 'email',
			},
			{
				...SUBSCRIBER_PAGE,
				name: 'Confirmation page',
				icon: 'page',
			},
		]
	}
}

export function buildWhereClause(
	filters: Record<string, string>,
	listId: string,
) {
	const whereClause: any = { listId }

	Object.entries(filters).forEach(([key, value]) => {
		const fieldName = key.replace('filter.', '')
		if (fieldName === 'isVerified' || fieldName === 'unsubscribed') {
			whereClause[fieldName] = value === 'true'
		} else if (fieldName === 'createdAt' || fieldName === 'updatedAt') {
			const dateRange = JSON.parse(value as string) as DateRange
			whereClause[fieldName] = {
				...(dateRange.from && { gte: new Date(dateRange.from) }),
				...(dateRange.to && { lte: new Date(dateRange.to) }),
			}
		} else if (fieldName === 'points' || fieldName === 'referrals') {
			const numberFilter = JSON.parse(value as string) as NumberFilter
			whereClause[fieldName] = {
				...(numberFilter.min && { gte: numberFilter.min }),
				...(numberFilter.max && { lte: numberFilter.max }),
			}
		} else {
			whereClause[fieldName] = {
				contains: String(value),
				mode: 'insensitive',
			}
		}
	})

	return whereClause
}
